import {
  takeLatest,
  put,
  call,
  CallEffect,
  PutEffect,
} from 'redux-saga/effects';
import { authActions } from 'features/auth/redux/auth.actions';
import {
  checkAuthStatusAPI,
  refreshAccessTokenAPI,
  signInAPI,
  signOutAPI,
} from 'features/auth/api';
import {
  getAccessTokenFromLocalStorage,
  removeAccessTokenFromLocalStorage,
  removePartnerHeaderFeaturesFromLocalStorage,
  removeRefreshTokenFromLocalStorage,
  setAccessTokenToLocalStorage,
  setRefreshTokenToLocalStorage,
} from 'utils/local-storage';
import { getArrayApiErrors } from 'utils/get-array-api-errors';
import { RefreshTokenResponse, SignInPayload } from 'features/auth/api/types';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { extractErrorMessage } from 'utils/extract-error-message.util';
import { processQueue } from 'features/auth/utils/refresh-queue';
import {
  resetAmplitude,
  setUserId,
  setUserProperties,
  trackEvent,
} from 'utils/amplitude.util';
import { AmplitudeEvents } from 'shared/enums/amplitude-events.enum';
import { extractUserDataFromJwtToken } from 'utils/extract-user-data-from-jwt-token';

function* checkAuthStatus() {
  try {
    yield call(checkAuthStatusAPI);
    const token = getAccessTokenFromLocalStorage();
    if (token) {
      const userData = extractUserDataFromJwtToken(token);
      if (userData.id) {
        setUserId(String(userData.id));
        setUserProperties({
          email: userData.email,
          role: userData.role?.name,
        });
      }
    }
    yield put(authActions.checkAuthSuccess());
  } catch (error) {
    yield put(authActions.checkAuthFailure());
    removeAccessTokenFromLocalStorage();
    removeRefreshTokenFromLocalStorage();
  }
}

function* signIn({
  payload,
}: {
  payload: SignInPayload;
}): Generator<any, void, AxiosResponse> {
  try {
    const response = yield call(signInAPI, payload);
    setAccessTokenToLocalStorage(response.data?.accessToken);
    if (response.data?.refreshToken) {
      setRefreshTokenToLocalStorage(response.data.refreshToken);
    } else {
      removeRefreshTokenFromLocalStorage();
    }
    const userData = extractUserDataFromJwtToken(response.data?.accessToken);
    if (userData.id) {
      setUserId(String(userData.id));
      setUserProperties({
        email: userData.email,
        role: userData.role?.name,
      });
    }
    trackEvent(AmplitudeEvents.LOGIN_SUCCESS, {
      user_role: userData.role?.name,
    });
    yield put(authActions.signInSuccess(response.data));
  } catch (error) {
    trackEvent(AmplitudeEvents.LOGIN_FAILED, {
      error_type: extractErrorMessage(error),
    });
    toast.error(extractErrorMessage(error));
    yield put(authActions.signInFailure(getArrayApiErrors(error)));
    removeAccessTokenFromLocalStorage();
    removeRefreshTokenFromLocalStorage();
  }
}

function* refreshAccessToken(): Generator<
  CallEffect<AxiosResponse<RefreshTokenResponse>> | PutEffect,
  void,
  AxiosResponse<RefreshTokenResponse>
> {
  try {
    const { data } = yield call(refreshAccessTokenAPI);
    setAccessTokenToLocalStorage(data?.accessToken);
    if (data?.refreshToken) {
      setRefreshTokenToLocalStorage(data.refreshToken);
    } else {
      removeRefreshTokenFromLocalStorage();
    }
    yield put(authActions.refreshTokenSuccess(data.accessToken));

    processQueue(null, data.accessToken);
  } catch (error) {
    processQueue(error, null);
    const status = error.response?.status;
    if ([401, 403].includes(status)) {
      yield put(authActions.signOutRequest());
    }
  }
}

function* signOut() {
  try {
    yield call(signOutAPI);
    removeAccessTokenFromLocalStorage();
    removeRefreshTokenFromLocalStorage();
    removePartnerHeaderFeaturesFromLocalStorage();
    resetAmplitude();
    yield put(authActions.signOutSuccess());
  } catch (error) {
    toast.error('Unable to log out. Please try again.');
    yield put(authActions.signOutFailure(getArrayApiErrors(error)));
  }
}

function* authSaga() {
  yield takeLatest(authActions.checkAuthRequest, checkAuthStatus);
  yield takeLatest(authActions.signInRequest, signIn);
  yield takeLatest(authActions.signOutRequest, signOut);
  yield takeLatest(authActions.refreshTokenRequest, refreshAccessToken);
}

export default authSaga;
